<template>
  <div
    class="header"
    :style="`background:${headerBgColor}`"
  >
    <div class="row m-0 header-row">
      <div
        v-if="showCheckboxes"
        class="col header-cell align-self-center checkbox flex-grow-0"
      >
        <RsFormCheckbox
          :id="`${id}_checkbox_all`"
          v-model="checkboxAll"
          :true-value="true"
          :false-value="false"
          @change="handleCheckboxAllClicked()"
        />
      </div>
      <div
        v-for="(field, index) in (fields.filter((v) => v.variant !== 'hidden'))"
        v-show="shouldShowHeader?.(field)"
        :key="index"
        class="col py-3 text-truncate"
        :class="`header-cell ${field.variant} ${field.isSortable ? 'sortable' : ''} ${field.isCentered ? 'centered' : ''} ${field.colClass ? field.colClass() : ''}`"
        :style="(field as any).width ? `width:${(field as any).width}` : ''"
        @click="handleSorting(field)"
      >
        <span
          v-if="field.isSortable"
          class="table-sort-container mr-1"
          :style="`${field.variant === 'status' && field.isSortable ? 'margin-left:10px;' : '' }`"
        >
          <RsIcon
            :class="`sort-icon--up${isFieldSortActive(field, 'desc') ? '--active' : ''}`"
            weight="fa"
            icon="chevron-up"
          />

          <RsIcon
            weight="fa"
            :class="`sort-icon--down${isFieldSortActive(field, 'asc') ? '--active' : ''}`"
            icon="chevron-down"
          />
        </span>

        {{ field.display }}

        <span v-if="field.titleHelperTooltip">
          <RsIcon
            :id="`helperTooltip-${index}`"
            icon="question-circle"
            class="question-icon"
          />
          <RsTooltip
            :target="`helperTooltip-${index}`"
            :title="field.titleHelperTooltip"
            position="top"
            :delay="{ show: 200 }"
          />
        </span>

        <div v-if="field.variant === 'radio'">
          <RsFormRadio
            :id="`${id}_data_table_radio_${(field.property.replace('?custom', ''))}_${index}_all`"
            v-model="radioAll"
            :value="field.trueValue"
            label="All"
            class="radio-all"
            @change="handleRadioAllClicked(field)"
          />
        </div>
      </div>
      <div
        v-if="hasDetailsView"
        class="col header-cell align-self-center flex-grow-0 details-view"
      />

      <div
        v-if="hasDropdown"
        class="col header-cell align-self-center flex-grow-0"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { IDataTableFieldConfig, IDataTableRouteQuery } from '@/shared';

export default defineComponent({
  name: 'RsPartialTableHeader',

  props: {
    fields: {
      type: Array as PropType<IDataTableFieldConfig[]>,
      default: () => [],
    },

    dataFilters: {
      type: Object as PropType<IDataTableRouteQuery>,
      default: () => ({ orderBy: '', orderDir: 'asc' }),
    },

    headerBgColor: {
      type: String,
      default: '#fff',
    },

    radioAllValue: {
      type: Object as PropType<any>,
      default: null,
    },

    checkboxAllValue: {
      type: Boolean,
      default: false,
    },

    showCheckboxes: Boolean,
    shouldShowHeader: Function,
    id: String,
    hasDetailsView: Boolean,
    hasDropdown: Boolean,
  },

  data() {
    return {
      radioAll: null as any,
      checkboxAll: false as boolean,
    };
  },

  watch: {
    radioAll: {
      immediate: true,
      deep: true,
      handler() {
        this.onRadioAllChange();
      },
    },

    checkboxAll: {
      immediate: true,
      deep: true,
      handler() {
        this.onCheckboxAllChange();
      },
    },

    checkboxAllValue: {
      immediate: true,
      deep: true,
      handler() {
        this.onCheckboxAllChange();
      },
    },

    radioAllValue: {
      immediate: true,
      deep: true,
      handler() {
        this.radioAll = this.radioAllValue;
      },
    },
  },

  methods: {
    isFieldSortActive(field: IDataTableFieldConfig, sortType: 'asc' | 'desc') {
      return this.dataFilters.orderBy === field.property.split('?')[0] && this.dataFilters.orderDir === sortType;
    },

    handleRadioAllClicked(field: IDataTableFieldConfig) {
      this.$emit('on-radio-all-clicked', { field });
      return { field };
    },

    handleCheckboxAllClicked() {
      this.$emit('on-checkbox-all-clicked');
    },

    onRadioAllChange() {
      this.$emit('on-radio-all-change', { radioAll: this.radioAll });
      return { radioAll: this.radioAll };
    },

    onCheckboxAllChange() {
      this.$emit('on-checkbox-all-change', { checkboxAll: this.checkboxAll });
      return { checkboxAll: this.checkboxAll };
    },

    handleSorting(field: IDataTableFieldConfig) {
      this.$emit('sorting-handler', { field });
      return { field };
    },
  },
});
</script>
